import { createItemIconProvider, dimColor } from '../pixel-icons.js';
import { makeStack, clearStack, isStackEmpty, getStackMax } from '../ui/stack-utils.js';
import { createContainerTooltip } from '../ui/container-shared.js';

export function createChestSystem({
  world,
  ui,
  itemDefs,
  t,
  onRequestClose = null
}) {
  const CHEST_BLOCK_ID = 14;
  const CHEST_SLOT_COUNT = 27;

  const chestStates = new Map();
  let openChestKey = null;
  const slotEls = [];
  const slotDragPlace = {
    active: false,
    button: 0,
    startIndex: -1,
    itemId: null,
    visited: new Set(),
    moved: false
  };

  const iconProvider = createItemIconProvider(itemDefs);
  const overlayEl = document.getElementById('chest-overlay');
  const titleEl = document.getElementById('chest-title');
  const hintEl = document.getElementById('chest-hint');
  const gridEl = document.getElementById('chest-grid');
  const takeAllBtnEl = document.getElementById('chest-take-all-btn');
  const closeBtnEl = document.getElementById('chest-close-btn');
  const tooltipEl = document.getElementById('inventory-tooltip');

  const { hideTooltip, bindSlotTooltip } = createContainerTooltip({ itemDefs, t, tooltipEl });

  function chestKeyFromPos(x, y, z) {
    return `${Math.round(x)},${Math.round(y)},${Math.round(z)}`;
  }

  function parseChestKey(key) {
    const [x, y, z] = String(key).split(',').map(Number);
    return { x, y, z };
  }

  function normalizeStackData(raw) {
    const itemId = String(raw?.id || '');
    if (!itemDefs[itemId]) return null;
    const count = Math.floor(Number(raw?.count));
    if (!Number.isFinite(count) || count <= 0) return null;
    return {
      id: itemId,
      count: Math.min(getStackMax(itemDefs, itemId), count)
    };
  }

  function hash3i(x, y, z, seed = 0) {
    let h = ((x | 0) * 374761393) ^ ((y | 0) * 1274126177) ^ ((z | 0) * 668265263) ^ ((seed | 0) * 1597334677);
    h = (h ^ (h >>> 13)) * 2246822519;
    return h >>> 0;
  }

  function createRng(seed) {
    let s = seed >>> 0;
    return function rand() {
      s = (s * 1664525 + 1013904223) >>> 0;
      return s / 4294967296;
    };
  }

  function randomIndexEmptySlot(slots, rand) {
    const empty = [];
    for (let i = 0; i < slots.length; i++) {
      if (isStackEmpty(slots[i])) empty.push(i);
    }
    if (empty.length <= 0) return -1;
    return empty[Math.floor(rand() * empty.length)];
  }

  function chooseLootTable(ecosystem) {
    const poiType = ecosystem?.poi?.type || null;
    const threat = Math.max(0, Number(ecosystem?.threat || 0));
    if (ecosystem?.shipwreck) {
      return [
        { id: 'iron_ingot', min: 1, max: 4, chance: 0.86 },
        { id: 'gold_ingot', min: 1, max: 3, chance: 0.72 },
        { id: 'emerald', min: 1, max: 2, chance: 0.5 },
        { id: 'coal', min: 2, max: 8, chance: 0.8 },
        { id: 'flint', min: 1, max: 4, chance: 0.66 },
        { id: 'arrow', min: 3, max: 12, chance: 0.72 },
        { id: 'bread', min: 1, max: 3, chance: 0.58 },
        { id: 'beef_cooked', min: 1, max: 2, chance: 0.36 },
        { id: 'pork_cooked', min: 1, max: 2, chance: 0.32 },
        { id: 'bone', min: 1, max: 4, chance: 0.5 },
        { id: 'shield', min: 1, max: 1, chance: 0.12 },
        { id: 'iron_sword', min: 1, max: 1, chance: 0.1 },
        { id: 'diamond_gem', min: 1, max: 1, chance: threat >= 3 ? 0.08 : 0.04 },
        { id: 'flint_steel', min: 1, max: 1, chance: 0.18 }
      ];
    }

    if (poiType === 'raider_outpost') {
      return [
        { id: 'arrow', min: 3, max: 14, chance: 0.82 },
        { id: 'bone', min: 1, max: 5, chance: 0.66 },
        { id: 'gunpowder', min: 1, max: 4, chance: 0.54 },
        { id: 'string', min: 1, max: 4, chance: 0.52 },
        { id: 'coal', min: 2, max: 9, chance: 0.7 },
        { id: 'emerald', min: 1, max: 2, chance: 0.28 },
        { id: 'iron_ingot', min: 1, max: 3, chance: 0.5 },
        { id: 'gold_ingot', min: 1, max: 2, chance: 0.34 },
        { id: 'iron_sword', min: 1, max: 1, chance: 0.16 },
        { id: 'bow', min: 1, max: 1, chance: 0.16 },
        { id: 'redstone_dust', min: 2, max: 8, chance: 0.22 },
        { id: 'wheat_seed', min: 2, max: 8, chance: 0.42 },
        { id: 'diamond_gem', min: 1, max: 1, chance: threat >= 3 ? 0.07 : 0.03 }
      ];
    }

    if (poiType === 'monster_den') {
      return [
        { id: 'bone', min: 2, max: 8, chance: 0.84 },
        { id: 'arrow', min: 3, max: 12, chance: 0.7 },
        { id: 'gunpowder', min: 1, max: 5, chance: 0.76 },
        { id: 'string', min: 2, max: 7, chance: 0.72 },
        { id: 'spider_eye', min: 1, max: 3, chance: 0.42 },
        { id: 'coal', min: 2, max: 8, chance: 0.62 },
        { id: 'iron_ingot', min: 1, max: 3, chance: 0.44 },
        { id: 'flint_steel', min: 1, max: 1, chance: 0.2 },
        { id: 'ender_eye', min: 1, max: 1, chance: threat >= 3 ? 0.16 : 0.08 },
        { id: 'diamond_gem', min: 1, max: 1, chance: threat >= 3 ? 0.09 : 0.04 }
      ];
    }

    if (poiType === 'ancient_ruin') {
      return [
        { id: 'coal', min: 2, max: 8, chance: 0.74 },
        { id: 'iron_ingot', min: 1, max: 3, chance: 0.56 },
        { id: 'gold_ingot', min: 1, max: 2, chance: 0.46 },
        { id: 'emerald', min: 1, max: 2, chance: 0.38 },
        { id: 'flint', min: 1, max: 4, chance: 0.58 },
        { id: 'bread', min: 1, max: 2, chance: 0.46 },
        { id: 'redstone_dust', min: 2, max: 9, chance: 0.32 },
        { id: 'flint_steel', min: 1, max: 1, chance: 0.2 },
        { id: 'ender_eye', min: 1, max: 1, chance: threat >= 2 ? 0.11 : 0.05 },
        { id: 'diamond_pickaxe', min: 1, max: 1, chance: threat >= 2 ? 0.04 : 0.015 },
        { id: 'diamond_gem', min: 1, max: 1, chance: threat >= 2 ? 0.07 : 0.03 }
      ];
    }

    if (poiType === 'hospital') {
      return [
        { id: 'medkit', min: 1, max: 2, chance: 0.88 },
        { id: 'painkiller', min: 1, max: 4, chance: 0.82 },
        { id: 'aloe', min: 2, max: 8, chance: 0.78 },
        { id: 'bread', min: 1, max: 3, chance: 0.6 },
        { id: 'apple', min: 1, max: 3, chance: 0.58 },
        { id: 'water_bucket', min: 1, max: 1, chance: 0.2 },
        { id: 'coal', min: 1, max: 5, chance: 0.42 },
        { id: 'iron_ingot', min: 1, max: 2, chance: 0.32 },
        { id: 'emerald', min: 1, max: 2, chance: 0.18 }
      ];
    }

    if (poiType === 'hamlet' || poiType === 'tribe_camp') {
      return [
        { id: 'bread', min: 1, max: 4, chance: 0.78 },
        { id: 'wheat', min: 1, max: 5, chance: 0.66 },
        { id: 'wheat_seed', min: 2, max: 8, chance: 0.72 },
        { id: 'coal', min: 1, max: 5, chance: 0.54 },
        { id: 'iron_ingot', min: 1, max: 2, chance: 0.3 },
        { id: 'emerald', min: 1, max: 2, chance: 0.24 },
        { id: 'arrow', min: 2, max: 8, chance: 0.45 }
      ];
    }

    return [
      { id: 'coal', min: 1, max: 6, chance: 0.74 },
      { id: 'bread', min: 1, max: 3, chance: 0.5 },
      { id: 'arrow', min: 1, max: 6, chance: 0.4 },
      { id: 'wheat_seed', min: 2, max: 8, chance: 0.46 },
      { id: 'iron_ingot', min: 1, max: 2, chance: 0.24 }
    ];
  }

  function populateGeneratedChest(key, slots) {
    const pos = parseChestKey(key);
    const block = world.getBlockAt(pos.x, pos.y, pos.z);
    if (!block?.userData?.generated) return;

    const ecosystem = typeof world.getEcosystemAt === 'function'
      ? world.getEcosystemAt(pos.x, pos.z)
      : null;
    const table = chooseLootTable(ecosystem);
    const seed = hash3i(pos.x, pos.y, pos.z, 7301);
    const rand = createRng(seed);

    const threat = Math.max(0, Number(ecosystem?.threat || 0));
    const bonusRolls = (ecosystem?.shipwreck ? 1 : 0) + (threat >= 2 ? 1 : 0);
    const rolls = 3 + Math.floor(rand() * 5) + bonusRolls;
    let placed = 0;
    for (let i = 0; i < rolls; i++) {
      const entry = table[Math.floor(rand() * table.length)];
      if (!entry || !itemDefs[entry.id]) continue;
      if (rand() > entry.chance) continue;

      const slotIdx = randomIndexEmptySlot(slots, rand);
      if (slotIdx < 0) break;

      const min = Math.max(1, Math.floor(entry.min || 1));
      const max = Math.max(min, Math.floor(entry.max || min));
      const count = min + Math.floor(rand() * (max - min + 1));
      slots[slotIdx].id = entry.id;
      slots[slotIdx].count = count;
      placed += 1;
    }

    if (placed <= 0) {
      const slotIdx = randomIndexEmptySlot(slots, rand);
      if (slotIdx >= 0) {
        const fallback = table[Math.floor(rand() * table.length)];
        slots[slotIdx].id = itemDefs[fallback?.id] ? fallback.id : 'bread';
        slots[slotIdx].count = 1;
      }
    }
  }

  function getOrCreateState(key) {
    if (!chestStates.has(key)) {
      const slots = Array.from({ length: CHEST_SLOT_COUNT }, () => makeStack());
      populateGeneratedChest(key, slots);
      chestStates.set(key, slots);
    }
    return chestStates.get(key);
  }

  function currentState() {
    if (!openChestKey) return null;
    return getOrCreateState(openChestKey);
  }

  function takeToInventory(itemId, count) {
    let moved = 0;
    const total = Math.max(0, Math.floor(count));
    for (let i = 0; i < total; i++) {
      if (!ui.addItem(itemId, 1)) break;
      moved += 1;
    }
    return moved;
  }

  function depositFromActive(slot, desiredCount) {
    const activeId = ui.getActiveItemId();
    const activeCount = ui.getActiveItemCount();
    if (!activeId || activeCount <= 0 || desiredCount <= 0) return 0;

    const maxStack = getStackMax(itemDefs, activeId);
    if (isStackEmpty(slot)) {
      const move = Math.min(desiredCount, activeCount, maxStack);
      if (move <= 0) return 0;
      if (!ui.consumeActiveItem(move)) return 0;
      slot.id = activeId;
      slot.count = move;
      return move;
    }

    if (slot.id !== activeId) return 0;
    const space = maxStack - slot.count;
    if (space <= 0) return 0;
    const move = Math.min(desiredCount, activeCount, space);
    if (move <= 0) return 0;
    if (!ui.consumeActiveItem(move)) return 0;
    slot.count += move;
    return move;
  }

  function withdrawToInventory(slot, desiredCount) {
    if (isStackEmpty(slot) || desiredCount <= 0) return 0;
    const move = Math.min(slot.count, desiredCount);
    const moved = takeToInventory(slot.id, move);
    if (moved <= 0) return 0;
    slot.count -= moved;
    if (slot.count <= 0) clearStack(slot);
    return moved;
  }

  function quickMoveIntoOpenChest(itemId, desiredCount) {
    if (!isOpen() || !itemDefs[itemId]) return 0;
    const state = currentState();
    if (!state) return 0;

    let remaining = Math.max(0, Math.floor(desiredCount));
    if (remaining <= 0) return 0;

    const maxStack = getStackMax(itemDefs, itemId);
    const stackable = maxStack > 1 && itemDefs[itemId]?.kind !== 'tool' && itemDefs[itemId]?.kind !== 'armor';

    if (stackable) {
      for (const slot of state) {
        if (remaining <= 0) break;
        if (isStackEmpty(slot) || slot.id !== itemId) continue;
        const space = maxStack - slot.count;
        if (space <= 0) continue;
        const add = Math.min(space, remaining);
        slot.count += add;
        remaining -= add;
      }
    }

    for (const slot of state) {
      if (remaining <= 0) break;
      if (!isStackEmpty(slot)) continue;
      const put = stackable ? Math.min(maxStack, remaining) : 1;
      slot.id = itemId;
      slot.count = put;
      remaining -= put;
    }

    const moved = Math.max(0, Math.floor(desiredCount) - remaining);
    if (moved > 0) renderOpenChest();
    return moved;
  }

  function collectSimilarToInventoryCursor({ itemId = null, maxTake = 0 } = {}) {
    const targetId = String(itemId || '');
    const takeLimit = Math.max(0, Math.floor(Number(maxTake)));
    if (!isOpen() || !targetId || takeLimit <= 0) return 0;

    const def = itemDefs[targetId];
    if (!def) return 0;
    const maxStack = getStackMax(itemDefs, targetId);
    if (def.kind === 'tool' || def.kind === 'armor' || maxStack <= 1) return 0;

    const state = currentState();
    if (!state) return 0;

    const candidates = [];
    let order = 0;
    for (const slot of state) {
      if (isStackEmpty(slot) || slot.id !== targetId) continue;
      candidates.push({
        slot,
        count: slot.count,
        partialRank: slot.count < maxStack ? 0 : 1,
        order: order++
      });
    }
    if (candidates.length <= 0) return 0;

    // Same priority as inventory double-collect: smaller partial stacks first, full stacks last.
    candidates.sort((a, b) => {
      if (a.partialRank !== b.partialRank) return a.partialRank - b.partialRank;
      if (a.partialRank === 0 && a.count !== b.count) return a.count - b.count;
      return a.order - b.order;
    });

    let remaining = takeLimit;
    let moved = 0;
    for (const entry of candidates) {
      if (remaining <= 0) break;
      const slot = entry.slot;
      if (isStackEmpty(slot) || slot.id !== targetId) continue;
      const take = Math.min(remaining, slot.count);
      if (take <= 0) continue;
      slot.count -= take;
      if (slot.count <= 0) clearStack(slot);
      remaining -= take;
      moved += take;
    }

    if (moved > 0) renderOpenChest();
    return moved;
  }

  function renderSlot(el, stack) {
    if (!el) return;
    if (!stack || !stack.id || stack.count <= 0) {
      el.className = 'slot chest-slot';
      el.style.backgroundColor = '#3d434f';
      el.innerHTML = '<span class="slot-key"></span><span class="slot-icon"></span><span class="slot-count"></span>';
      el.title = '';
      return;
    }

    const def = itemDefs[stack.id];
    const iconUrl = iconProvider.getIcon(stack.id);
    el.className = 'slot chest-slot';
    el.style.backgroundColor = dimColor(def?.color, 0.35) || '#2a2e36';
    el.innerHTML = `
      <span class="slot-key"></span>
      <span class="slot-icon">${iconUrl ? `<img src="${iconUrl}" alt="" draggable="false">` : ''}</span>
      <span class="slot-count">${stack.count > 1 ? stack.count : ''}</span>
    `;
    el.title = '';
  }

  function renderOpenChest() {
    if (!overlayEl || !isOpen()) return;
    const state = currentState();
    if (!state) return;

    if (titleEl) titleEl.innerText = t('chest.title');
    if (hintEl) hintEl.innerText = t('chest.hint');
    if (takeAllBtnEl) takeAllBtnEl.innerText = t('chest.take_all');
    if (closeBtnEl) closeBtnEl.innerText = t('chest.close');

    for (let i = 0; i < slotEls.length; i++) {
      renderSlot(slotEls[i], state[i]);
    }
  }

  function stopSlotDragPlace() {
    slotDragPlace.active = false;
    slotDragPlace.button = 0;
    slotDragPlace.startIndex = -1;
    slotDragPlace.itemId = null;
    slotDragPlace.visited.clear();
    slotDragPlace.moved = false;
  }

  function canStartSlotDrag(slot, cursorItemId = null) {
    const itemId = String(cursorItemId || '');
    if (!itemId || !itemDefs[itemId]) return false;
    const def = itemDefs[itemId];
    const maxStack = getStackMax(itemDefs, itemId);
    if (def.kind === 'tool' || def.kind === 'armor' || maxStack <= 1) return false;
    if (isStackEmpty(slot)) return true;
    return slot.id === itemId && slot.count < maxStack;
  }

  function startSlotDragPlace(index, button, itemId) {
    slotDragPlace.active = true;
    slotDragPlace.button = button;
    slotDragPlace.startIndex = index;
    slotDragPlace.itemId = itemId;
    slotDragPlace.visited.clear();
    slotDragPlace.moved = false;
  }

  function continueSlotDragPlace(index, buttons = 0) {
    if (!slotDragPlace.active) return;
    if (
      (slotDragPlace.button === 0 && (buttons & 1) === 0)
      || (slotDragPlace.button === 2 && (buttons & 2) === 0)
    ) {
      finalizeSlotDragPlace();
      return;
    }

    const state = currentState();
    if (!state) {
      stopSlotDragPlace();
      return;
    }

    const cursorItemId = ui.getCursorItemId?.();
    if (!cursorItemId || cursorItemId !== slotDragPlace.itemId) {
      stopSlotDragPlace();
      return;
    }

    const currentSlot = state[index];
    if (!currentSlot || slotDragPlace.visited.has(index) || !canStartSlotDrag(currentSlot, cursorItemId)) return;

    if (!slotDragPlace.moved) {
      const startSlot = state[slotDragPlace.startIndex];
      if (!startSlot || ui.placeOneIntoExternalSlot?.(startSlot) <= 0) {
        stopSlotDragPlace();
        return;
      }
      slotDragPlace.visited.add(slotDragPlace.startIndex);
      slotDragPlace.moved = true;
      renderOpenChest();
    }

    if (index === slotDragPlace.startIndex) return;
    if (ui.placeOneIntoExternalSlot?.(currentSlot) > 0) {
      slotDragPlace.visited.add(index);
      slotDragPlace.moved = true;
      renderOpenChest();
    }
  }

  function finalizeSlotDragPlace(e = null) {
    if (!slotDragPlace.active) return false;

    const state = currentState();
    if (!state) {
      stopSlotDragPlace();
      return false;
    }

    const releasedInsideChest = e?.target instanceof Element
      ? !!e.target.closest('#chest-window')
      : false;

    if (!slotDragPlace.moved && releasedInsideChest && slotDragPlace.startIndex >= 0) {
      const slot = state[slotDragPlace.startIndex];
      if (slot) {
        ui.handleExternalSlotMouseDown(slot, {
          button: slotDragPlace.button,
          shiftKey: false
        }, {
          onChange: renderOpenChest
        });
      }
    }

    stopSlotDragPlace();
    return true;
  }

  function handleSlotMouseDown(index, e) {
    const state = currentState();
    if (!state) return;
    const slot = state[index];
    if (!slot) return;

    const cursorItemId = ui.getCursorItemId?.();
    if (!e.shiftKey && (e.button === 0 || e.button === 2) && canStartSlotDrag(slot, cursorItemId)) {
      startSlotDragPlace(index, e.button, cursorItemId);
      return;
    }

    ui.handleExternalSlotMouseDown(slot, e, {
      onChange: renderOpenChest
    });
  }

  function ensureSlotDom() {
    if (!gridEl || slotEls.length > 0) return;
    gridEl.innerHTML = '';

    for (let i = 0; i < CHEST_SLOT_COUNT; i++) {
      const el = document.createElement('div');
      el.className = 'slot chest-slot';
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        handleSlotMouseDown(i, e);
      });
      el.addEventListener('mouseenter', (e) => continueSlotDragPlace(i, e.buttons));
      el.addEventListener('mousemove', (e) => continueSlotDragPlace(i, e.buttons));
      el.addEventListener('contextmenu', (e) => e.preventDefault());
      bindSlotTooltip(el, () => currentState()?.[i] || null);
      gridEl.appendChild(el);
      slotEls.push(el);
    }
  }

  function takeAllFromChest() {
    const state = currentState();
    if (!state) return false;

    let movedAny = false;
    for (const slot of state) {
      if (isStackEmpty(slot)) continue;
      if (withdrawToInventory(slot, slot.count) > 0) movedAny = true;
    }
    if (movedAny) renderOpenChest();
    return movedAny;
  }

  function ensureOpenChestStillExists() {
    if (!openChestKey) return;
    const pos = parseChestKey(openChestKey);
    if (world.getBlockIdAt(pos.x, pos.y, pos.z) === CHEST_BLOCK_ID) return;
    chestStates.delete(openChestKey);
    close();
  }

  function ejectContentsAt(x, y, z, onDrop = null) {
    const key = chestKeyFromPos(x, y, z);
    const state = chestStates.get(key);
    if (!state) return false;

    const pos = parseChestKey(key);
    for (const slot of state) {
      if (isStackEmpty(slot)) continue;
      if (typeof onDrop === 'function') {
        onDrop({
          itemId: slot.id,
          count: slot.count,
          position: pos
        });
      }
    }

    chestStates.delete(key);
    if (openChestKey === key) close();
    return true;
  }

  function openAtBlock(block) {
    if (!block || block.userData?.id !== CHEST_BLOCK_ID) return false;
    ensureSlotDom();
    const key = chestKeyFromPos(block.position.x, block.position.y, block.position.z);
    openChestKey = key;
    getOrCreateState(key);
    if (overlayEl) overlayEl.style.display = 'flex';
    document.body.classList.add('chest-open');
    document.body.classList.add('inventory-open');
    renderOpenChest();
    return true;
  }

  function close() {
    stopSlotDragPlace();
    openChestKey = null;
    if (overlayEl) overlayEl.style.display = 'none';
    hideTooltip();
    document.body.classList.remove('chest-open');
    if (!ui.isCraftOpen()) document.body.classList.remove('inventory-open');
  }

  function isOpen() {
    return !!openChestKey;
  }

  function refreshLocale() {
    renderOpenChest();
  }

  function update() {
    ensureOpenChestStillExists();
  }

  function exportState() {
    const chests = [];
    for (const [key, state] of chestStates.entries()) {
      const items = [];
      for (let i = 0; i < state.length; i++) {
        const slot = state[i];
        if (isStackEmpty(slot)) continue;
        items.push({
          index: i,
          id: slot.id,
          count: slot.count
        });
      }
      chests.push({ key, items });
    }
    return { chests };
  }

  function importState(snapshot = null) {
    chestStates.clear();
    const list = Array.isArray(snapshot?.chests) ? snapshot.chests : [];

    for (const entry of list) {
      const key = String(entry?.key || '');
      if (!key) continue;

      const slots = Array.from({ length: CHEST_SLOT_COUNT }, () => makeStack());
      const items = Array.isArray(entry?.items) ? entry.items : [];
      for (const item of items) {
        const index = Math.floor(Number(item?.index));
        if (!Number.isFinite(index) || index < 0 || index >= CHEST_SLOT_COUNT) continue;
        const normalized = normalizeStackData(item);
        if (!normalized) continue;
        slots[index].id = normalized.id;
        slots[index].count = normalized.count;
      }
      chestStates.set(key, slots);
    }

    ensureOpenChestStillExists();
    if (isOpen()) renderOpenChest();
  }

  function quickMoveFromInventory({ itemId = null, count = 0 } = {}) {
    if (!itemId || count <= 0) return 0;
    return quickMoveIntoOpenChest(String(itemId), count);
  }

  if (takeAllBtnEl) takeAllBtnEl.addEventListener('click', () => takeAllFromChest());
  if (closeBtnEl) closeBtnEl.addEventListener('click', () => {
    if (typeof onRequestClose === 'function') onRequestClose();
    else close();
  });
  document.addEventListener('mouseup', (e) => finalizeSlotDragPlace(e));

  ensureSlotDom();

  return {
    openAtBlock,
    close,
    isOpen,
    refreshLocale,
    update,
    quickMoveFromInventory,
    collectSimilarToInventoryCursor,
    ejectContentsAt,
    exportState,
    importState
  };
}
